import React, { useState } from 'react';
import { Check, X, ChevronDown } from 'lucide-react';
import { ROLE_FAMILIES, SENIORITY_BANDS, inferRoleFamily, inferSeniorityBand, inferSkills } from '../lib/profileInference';

export default function JobMetadataForm({ jobTitle, initialMetadata, onSubmit, onCancel }) {
  const inferred = jobTitle ? {
    role_family: inferRoleFamily(jobTitle, []),
    seniority_band: inferSeniorityBand(jobTitle, []),
    skills: inferSkills(jobTitle, []),
  } : {};

  const [roleFamily, setRoleFamily] = useState(initialMetadata?.role_family || inferred.role_family || 'Other');
  const [seniority, setSeniority] = useState(initialMetadata?.seniority_band || inferred.seniority_band || 'mid');
  const [requiredSkills, setRequiredSkills] = useState(initialMetadata?.required_skills || inferred.skills || []);
  const [preferredSkills, setPreferredSkills] = useState(initialMetadata?.preferred_skills || []);
  const [remoteAllowed, setRemoteAllowed] = useState(initialMetadata?.remote_allowed ?? true);
  const [newRequiredSkill, setNewRequiredSkill] = useState('');
  const [newPreferredSkill, setNewPreferredSkill] = useState('');

  const formatSeniority = (band) => {
    const map = { intern: 'Intern', junior: 'Junior', mid: 'Mid-Level', senior: 'Senior', lead: 'Lead', director: 'Director', vp: 'VP', c_level: 'C-Level' };
    return map[band] || band;
  };

  const addSkill = (list, setList, input, setInput) => {
    const skill = input.trim();
    if (skill && !list.includes(skill)) {
      setList([...list, skill]);
      setInput('');
    }
  };

  const handleSubmit = () => {
    onSubmit({
      role_family: roleFamily,
      seniority_band: seniority,
      required_skills: requiredSkills,
      preferred_skills: preferredSkills,
      remote_allowed: remoteAllowed,
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
        <h2 className="text-white font-bold">Job Details</h2>
        {jobTitle && <p className="text-cyan-100 text-xs mt-0.5">{jobTitle}</p>}
      </div>

      <div className="p-4 space-y-4">
        {/* Role Family */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Role Family <span className="text-red-500">*</span></label>
          <div className="relative">
            <select value={roleFamily} onChange={(e) => setRoleFamily(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm appearance-none focus:border-vouch-teal focus:outline-none bg-white">
              {ROLE_FAMILIES.map(rf => <option key={rf} value={rf}>{rf}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Seniority */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Seniority Level</label>
          <div className="relative">
            <select value={seniority} onChange={(e) => setSeniority(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm appearance-none focus:border-vouch-teal focus:outline-none bg-white">
              {SENIORITY_BANDS.map(sb => <option key={sb} value={sb}>{formatSeniority(sb)}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Required Skills */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Required Skills</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {requiredSkills.map(skill => (
              <span key={skill} className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full font-medium">
                {skill}
                <button onClick={() => setRequiredSkills(requiredSkills.filter(s => s !== skill))} className="hover:text-red-900"><X size={12} /></button>
              </span>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); addSkill(requiredSkills, setRequiredSkills, newRequiredSkill, setNewRequiredSkill); }} className="flex gap-2">
            <input type="text" value={newRequiredSkill} onChange={(e) => setNewRequiredSkill(e.target.value)} placeholder="Add required skill..."
              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-vouch-teal focus:outline-none" />
            <button type="submit" disabled={!newRequiredSkill.trim()} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50">Add</button>
          </form>
        </div>

        {/* Preferred Skills */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Preferred Skills</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {preferredSkills.map(skill => (
              <span key={skill} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">
                {skill}
                <button onClick={() => setPreferredSkills(preferredSkills.filter(s => s !== skill))} className="hover:text-blue-900"><X size={12} /></button>
              </span>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); addSkill(preferredSkills, setPreferredSkills, newPreferredSkill, setNewPreferredSkill); }} className="flex gap-2">
            <input type="text" value={newPreferredSkill} onChange={(e) => setNewPreferredSkill(e.target.value)} placeholder="Add preferred skill..."
              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-vouch-teal focus:outline-none" />
            <button type="submit" disabled={!newPreferredSkill.trim()} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50">Add</button>
          </form>
        </div>

        {/* Remote Toggle */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Remote Allowed</label>
          <div className="flex gap-2">
            <button onClick={() => setRemoteAllowed(true)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${remoteAllowed ? 'bg-vouch-teal text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Yes
            </button>
            <button onClick={() => setRemoteAllowed(false)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${!remoteAllowed ? 'bg-vouch-teal text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              No
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200">Cancel</button>
          <button onClick={handleSubmit} className="flex-1 py-2.5 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5">
            <Check size={16} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
